import { useState } from 'react';
import { Dialog, CircularProgress, TextField } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import ImageField from './imageField';
import { Eyebrow } from '../../atome/motion/motion.styled';
import { ProductModel, ProductPayload } from '../../models/product/product';
import {
  DialogBody,
  DialogFoot,
  DialogHead,
  ErrorBanner,
  FieldRow,
  GhostButton,
  PricePreview,
  PrimaryButton,
  dialogPaperSx,
  fieldSx,
} from './admin.styled';

/** The form works in plain strings; the API wants numbers and arrays. */
interface FormValues {
  name: string;
  category: string;
  price: string;
  discount: string;
  stock: string;
  /** One bullet per line — stored joined with "|". */
  description: string;
  /** Server paths returned by the upload endpoint; the first is the cover. */
  images: string[];
}

const schema = Yup.object().shape({
  name: Yup.string().trim().required('A name is required'),
  category: Yup.string().trim().max(60, 'Keep the category short'),
  price: Yup.number()
    .typeError('Price must be a number')
    .required('Price is required')
    .moreThan(0, 'Price must be above 0'),
  discount: Yup.number()
    .typeError('Discount must be a number')
    .min(0, 'Discount cannot be negative')
    .max(100, 'Discount cannot pass 100%'),
  stock: Yup.number()
    .typeError('Quantity must be a number')
    .required('Quantity is required')
    .integer('Use whole units')
    .min(0, 'Quantity cannot be negative'),
  description: Yup.string().trim().required('Add at least one description line'),
});

const toFormValues = (product: ProductModel | null): FormValues => ({
  name: product?.name || '',
  category: product?.category || '',
  price: product ? String(product.price) : '',
  discount: product ? String(product.discount) : '0',
  stock: product ? String(product.stock) : '0',
  description: (product?.description || '')
    .split('|')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n'),
  images: product?.imageUrl || [],
});

const toPayload = (values: FormValues): ProductPayload => ({
  name: values.name.trim(),
  category: values.category.trim(),
  price: Number(values.price),
  discount: Number(values.discount) || 0,
  stock: Number(values.stock) || 0,
  // Bullets travel as a single "|"-joined string, the shape the storefront reads.
  description: values.description
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('|'),
  imageUrl: values.images.filter(Boolean),
});

const round2 = (value: number) => Math.round(value * 100) / 100;

interface Props {
  open: boolean;
  /** null = creating a new product. */
  product: ProductModel | null;
  onClose: () => void;
  onSave: (payload: ProductPayload) => Promise<void>;
}

/**
 * Create / edit sheet. One form covers everything an admin can change:
 * name, category, price, discount, quantity in stock, description and images.
 */
const ProductForm = ({ open, product, onClose, onSave }: Props) => {
  const [error, setError] = useState('');
  const isEdit = Boolean(product);

  const handleSubmit = async (values: FormValues) => {
    setError('');
    try {
      await onSave(toPayload(values));
    } catch (err: any) {
      setError(err?.message || 'Could not save this product.');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: dialogPaperSx } }}
    >
      <Formik
        initialValues={toFormValues(product)}
        validationSchema={schema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => {
          const price = Number(values.price) || 0;
          const discount = Math.min(Math.max(Number(values.discount) || 0, 0), 100);
          const finalPrice = round2(price * (1 - discount / 100));

          return (
            <Form noValidate>
              <DialogHead>
                <Eyebrow>Klarity</Eyebrow>
                <h2>{isEdit ? 'Edit product' : 'New product'}</h2>
                <p>
                  {isEdit
                    ? 'Changes go live on the shop as soon as you save.'
                    : 'It appears on the shop the moment it is created.'}
                </p>
              </DialogHead>

              <DialogBody>
                {error && (
                  <ErrorBanner role="alert">
                    <ErrorOutline fontSize="small" />
                    {error}
                  </ErrorBanner>
                )}

                <TextField
                  fullWidth
                  sx={fieldSx}
                  name="name"
                  label="Name"
                  autoFocus
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <TextField
                  fullWidth
                  sx={fieldSx}
                  name="category"
                  label="Category"
                  placeholder="Skincare"
                  value={values.category}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.category && Boolean(errors.category)}
                  helperText={touched.category && errors.category}
                />

                <FieldRow>
                  <TextField
                    fullWidth
                    sx={fieldSx}
                    name="price"
                    label="Price ($)"
                    type="number"
                    inputProps={{ min: 0, step: '0.01' }}
                    value={values.price}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.price && Boolean(errors.price)}
                    helperText={touched.price && errors.price}
                  />

                  <TextField
                    fullWidth
                    sx={fieldSx}
                    name="discount"
                    label="Discount (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    value={values.discount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.discount && Boolean(errors.discount)}
                    helperText={
                      (touched.discount && errors.discount) || '0 means no discount'
                    }
                  />
                </FieldRow>

                <PricePreview>
                  <span>Shelf price</span>
                  <span>
                    <strong>${finalPrice.toFixed(2)}</strong>
                    {discount > 0 && <del>${price.toFixed(2)}</del>}
                  </span>
                </PricePreview>

                <TextField
                  fullWidth
                  sx={fieldSx}
                  name="stock"
                  label="Quantity in stock"
                  type="number"
                  inputProps={{ min: 0, step: 1 }}
                  value={values.stock}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.stock && Boolean(errors.stock)}
                  helperText={
                    (touched.stock && errors.stock) || 'Set to 0 to show it as sold out'
                  }
                />

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  sx={fieldSx}
                  name="description"
                  label="Description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && Boolean(errors.description)}
                  helperText={
                    (touched.description && errors.description) ||
                    'One line per bullet point on the product page'
                  }
                />

                <ImageField
                  value={values.images}
                  onChange={(next) => setFieldValue('images', next)}
                  disabled={isSubmitting}
                />
              </DialogBody>

              <DialogFoot>
                <GhostButton type="button" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </GhostButton>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <CircularProgress size={17} sx={{ color: '#fff' }} />
                  ) : isEdit ? (
                    'Save changes'
                  ) : (
                    'Create product'
                  )}
                </PrimaryButton>
              </DialogFoot>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ProductForm;
